import { useState, useEffect, useRef } from "react";
import {
  getSystemVersionInfo,
  checkSystemUpdates,
  getSystemUpdateStatus,
  updateGithubToken,
  performFullUpdate,
} from "../../services/system.api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

// D8.10.2 — Lógica (estados + polling + update) de UpdateVersionModal.
export default function useUpdateVersion({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [versionInfo, setVersionInfo] = useState(null);
  const [error, setError] = useState(null);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(""); // Paso actual de la actualización
  // ✅ FASE 4: Estados para modal de token de GitHub
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [githubToken, setGithubToken] = useState("");
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  
  // ✅ REFS para limpiar intervalos
  const pollIntervalRef = useRef(null);
  
  // ✅ LIMPIAR INTERVALOS al desmontar componente
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleCheckUpdates = async () => {
    setLoading(true);
    setError(null);
    setUpdateStatus(null);
    setVersionInfo(null);

    try {
      // Obtener información de actualizaciones
      const { data: updateData } = await checkSystemUpdates();
      setUpdateStatus(updateData);
      
      // Obtener información de versiones semánticas
      try {
        const { data: versionData } = await getSystemVersionInfo();
        setVersionInfo(versionData);
      } catch (versionError) {
        console.log('⚠️ No se pudo obtener información de versiones:', versionError);
        // No es crítico, continuar sin información de versiones
      }
    } catch (err) {
      // ✅ MEJORAR MENSAJE DE ERROR: Usar información detallada del backend
      const errorData = err.response?.data || {};
      const errorInfo = {
        error: errorData.error || "Error al verificar actualizaciones",
        details: errorData.details || errorData.errorDetails || null,
        recommendation: errorData.recommendation || errorData.errorRecommendation || null,
        errorType: errorData.errorType || null
      };
      setError(errorInfo);
      
      // ✅ FASE 4: Si el error es de token inválido, mostrar modal para ingresar token
      if (errorData.errorType === "GITHUB_TOKEN_INVALID") {
        setShowTokenModal(true);
      } else {
        toastError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ FASE 4: Función para actualizar token de GitHub
  const handleUpdateGithubToken = async () => {
    const trimmedToken = githubToken.trim();
    
    // ✅ FASE 5: Validaciones mejoradas
    if (!trimmedToken) {
      setTokenError("El token es requerido");
      return;
    }

    // Validación de longitud mínima
    if (trimmedToken.length < 50) {
      setTokenError("El token parece ser demasiado corto. Un token válido de GitHub PAT tiene al menos 50 caracteres");
      return;
    }

    // Validación del formato del token (debe comenzar con github_pat_)
    if (!trimmedToken.startsWith("github_pat_")) {
      setTokenError("El token debe comenzar con 'github_pat_'. Verifica que estés usando un Personal Access Token (PAT) de GitHub");
      return;
    }

    // Validación de formato básico (github_pat_ seguido de caracteres alfanuméricos y guiones)
    const tokenPattern = /^github_pat_[A-Za-z0-9_-]+$/;
    if (!tokenPattern.test(trimmedToken)) {
      setTokenError("El formato del token no es válido. Asegúrate de copiar el token completo sin espacios adicionales");
      return;
    }

    setTokenLoading(true);
    setTokenError(null);

    try {
      await updateGithubToken({
        token: githubToken.trim()
      });

      // Si es exitoso, cerrar modal y reintentar verificación
      setShowTokenModal(false);
      setGithubToken("");
      setError(null);
      
      // Mostrar mensaje de éxito
      toast.success("✅ Token de GitHub actualizado exitosamente");
      
      // Reintentar verificación de actualizaciones
      await handleCheckUpdates();
    } catch (err) {
      const errorData = err.response?.data || {};
      setTokenError(errorData.error || errorData.details || "Error al actualizar el token");
      toastError(err);
    } finally {
      setTokenLoading(false);
    }
  };

  // ✅ NOTA: handleForceUpdate fue eliminada - El botón "FORZAR RECOMPILACIÓN" fue removido por UX
  // Si se necesita en el futuro, esta función puede restaurarse desde el historial de Git

  // ✅ POLLING: Verificar estado de actualización cada 3 segundos
  const pollUpdateStatus = () => {
    console.log("🚀 [POLLING] Iniciando polling de estado de actualización...");
    
    const checkStatus = async () => {
      try {
        console.log("📡 [POLLING] Verificando estado...");
        const { data: statusData } = await getSystemUpdateStatus();
        
        console.log(`📊 [POLLING] Respuesta: status=${statusData.status}, progress=${statusData.progress}%, step="${statusData.currentStep}"`);
        
        // Actualizar progreso y estado
        setUpdateProgress(statusData.progress || 0);
        setCurrentStep(statusData.currentStep || "");
        
        if (statusData.status === 'completed') {
          console.log("✅ [POLLING] Actualización completada!");
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setUpdating(false);
          setUpdateStatus(prev => ({
            ...prev,
            updateCompleted: true,
            newVersion: statusData.result?.newVersion,
            newMessage: statusData.result?.newMessage,
            newAuthor: statusData.result?.newAuthor,
            newDate: statusData.result?.newDate,
            steps: statusData.steps || []
          }));
        } else if (statusData.status === 'error') {
          console.log("❌ [POLLING] Error detectado:", statusData.error);
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setUpdating(false);
          setError({
            error: "Error durante la actualización",
            details: statusData.error || "Error desconocido",
            recommendation: "Verifica los logs del servidor para más detalles."
          });
        } else if (statusData.status === 'running') {
          console.log(`🔄 [POLLING] En progreso... Steps: ${statusData.steps?.length || 0}`);
          // Actualizar pasos completados
          setUpdateStatus(prev => ({
            ...prev,
            steps: statusData.steps || prev?.steps || []
          }));
        }
      } catch (err) {
        // ✅ Manejar errores de conexión durante reinicio (esperados)
        if (err.code === 'ECONNABORTED' || 
            err.message?.includes('timeout') || 
            err.message?.includes('Network Error') ||
            !err.response) {
          // Error de conexión esperado durante reinicio - continuar polling
          console.log("🔄 [POLLING] Backend reiniciando, continuando polling...");
        } else {
          console.error("❌ [POLLING] Error verificando estado:", err);
        }
        // Continuar polling aunque falle (el backend se restaurará)
      }
    };
    
    // Verificar inmediatamente y luego cada 3 segundos
    checkStatus();
    pollIntervalRef.current = setInterval(checkStatus, 3000);
  };

  const handlePerformUpdate = async (forceUpdate = false) => {
    // Si no hay actualizaciones y no es forzado, mostrar error
    if (!updateStatus?.hasUpdates && !forceUpdate) {
      setError("No hay actualizaciones disponibles");
      return;
    }

    setUpdating(true);
    setError(null);
    setUpdateProgress(0);

    try {
      console.log("🚀 [UPDATE] Iniciando actualización completa...");
      // Iniciar actualización (responde inmediatamente)
      const { data } = await performFullUpdate({
        previousVersion: updateStatus?.currentVersion || "N/A"
      });
      
      console.log("📨 [UPDATE] Respuesta del servidor:", data);
      
      if (data.status === 'running') {
        console.log("✅ [UPDATE] Servidor respondió 'running', iniciando polling...");
        // Iniciar polling para verificar progreso
        pollUpdateStatus();
        return; // Salir, el polling manejará el resto
      }

    } catch (err) {
      // Manejo especial para errores de conexión durante reinicio
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message?.includes('Network Error')) {
        // Esperar y verificar si la actualización se completó
        setTimeout(async () => {
          try {
            const { data: checkResult } = await checkSystemUpdates();
            // Si el sistema responde, la actualización fue exitosa
            const commitMessage = checkResult.currentCommitMessage || checkResult.currentMessage || "Actualización completada (conexión restaurada)";
            setUpdateProgress(100);
            setUpdateStatus({
              ...updateStatus,
              updateCompleted: true,
              newVersion: checkResult.currentVersion,
              newMessage: commitMessage,
              newAuthor: checkResult.currentAuthor || "Sistema",
              newDate: checkResult.currentDate || new Date().toLocaleDateString(),
              steps: ["✅ Actualización aplicada", "✅ Servicios reiniciados", "✅ Conexión restaurada"]
            });
          } catch (retryErr) {
            // Después del timeout, asumir que la actualización fue exitosa
            try {
              const { data: checkResult } = await checkSystemUpdates();
              const commitMessage = checkResult.currentCommitMessage || checkResult.currentMessage || "Actualización completada exitosamente";
              setError(null);
              setUpdateProgress(100);
              setUpdateStatus({
                ...updateStatus,
                updateCompleted: true,
                newVersion: checkResult.currentVersion || "Actualizada",
                newMessage: commitMessage,
                newAuthor: checkResult.currentAuthor || "Sistema", 
                newDate: checkResult.currentDate || new Date().toLocaleDateString(),
                steps: [
                  "✅ Código actualizado",
                  "✅ Dependencias actualizadas",
                  "✅ Backend compilado y reiniciado",
                  "✅ Servicios verificados",
                  "✅ Actualización completada (verificación manual recomendada)"
                ]
              });
            } catch (checkErr) {
              // Si no se puede obtener, usar mensaje genérico
              setError(null);
              setUpdateProgress(100);
              setUpdateStatus({
                ...updateStatus,
                updateCompleted: true,
                newVersion: "Actualizada",
                newMessage: "Actualización completada exitosamente",
                newAuthor: "Sistema", 
                newDate: new Date().toLocaleDateString(),
                steps: [
                  "✅ Código actualizado",
                  "✅ Dependencias actualizadas",
                  "✅ Backend compilado y reiniciado",
                  "✅ Servicios verificados",
                  "✅ Actualización completada (verificación manual recomendada)"
                ]
              });
            }
          }
        }, 10000); // Esperar 10 segundos antes de verificar
      } else {
        // ✅ MEJORAR MENSAJE DE ERROR: Usar información detallada del backend
        const errorData = err.response?.data || {};
        setError({
          error: errorData.error || "Error durante la actualización",
          details: errorData.details || errorData.errorDetails || null,
          recommendation: errorData.recommendation || errorData.errorRecommendation || null,
          errorType: errorData.errorType || null
        });
        toastError(err);
        setUpdateProgress(0);
        setUpdating(false);
      }
    }
  };

  const handleClose = () => {
    if (!updating) {
      setUpdateStatus(null);
      setError(null);
      setUpdateProgress(0);
      onClose();
      // Actualizar toda la página después de cerrar el modal
      setTimeout(() => {
        window.location.reload();
      }, 300); // Pequeño delay para que se cierre el modal primero
    }
  };

  return {
    currentStep,
    error,
    githubToken,
    handleCheckUpdates,
    handleClose,
    handlePerformUpdate,
    handleUpdateGithubToken,
    loading,
    pollIntervalRef,
    pollUpdateStatus,
    setCurrentStep,
    setError,
    setGithubToken,
    setLoading,
    setShowTokenModal,
    setTokenError,
    setTokenLoading,
    setUpdateProgress,
    setUpdateStatus,
    setUpdating,
    setVersionInfo,
    showTokenModal,
    tokenError,
    tokenLoading,
    updateProgress,
    updateStatus,
    updating,
    versionInfo,
  };
}
